package Structs

import DataBase.UINKDBInterface
import Utils.DatesManipulator
import Utils.OrderConstraints
import Utils.WrappedName
import Utils.getJsonWithConfig
import Utils.timeLock
import Utils.weekdayHourMapValue
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import myName
import printPlat

@myName("OrderProduct")
@Serializable
data class OrderProduct(
    val id: Int,
    val product_id: Int,
    val extra_unit: Int,
    val conversion_rate: Float,
    val weight: Float,
    val volume: Float,
    val englishName: String = "",
    var available: Byte,
    var active: Byte,
    val orderName: String = "",
    val position: Int = 0,
    val sales: Int = 0,
    val forceLock: Int = 0,
    val hourLock: String = "",
    val dayLock: Int = 63, // 6 bits
    val step: Float = 0f,
    val collector: Int = -1,
    val min_order: Float = 0f,
    val armor: Float = 0f,
    val days_ahead: Int = 0,
    val unit_preferece: Int = 0, // 0 - default , 1 - first unit , 2 - second unit
    val third_unit: Int = 0, // order-only label unit (WrappedName id), no conversion rate
    /** JSON keyed by the ISRAEL weekday the order is PLACED on (1=Sunday..7=Saturday): {"1":"14:00","6":"11:00"}.
     * Per-product override of [hourLock] for that ordering weekday; falls back to [hourLock], then to
     * [Structs.CofcConf.day_time_locks] for the same weekday, when absent/blank for the day. */
    @Serializable(with = BlockedDatesJsonStringSerializer::class)
    val day_hour_locks: String? = null,
    /** JSON keyed by DELIVERY weekday (1=Sunday..7=Saturday): {"1":{"weekday":5,"time":"14:00"}}.
     * Defines a recurring "order until weekday+time" rule for that delivery weekday (e.g. delivery
     * weekday 1/Sunday closes for ordering on weekday 5/Thursday at 14:00), replacing this product's
     * [days_ahead]/[hourLock]/[day_hour_locks] precedence chain for delivery dates that fall on a
     * configured weekday. The client's enabled delivery weekdays ([dayLock]) and company-wide date
     * restrictions still apply on top of this rule. Absent/blank keys fall back to the existing chain. */
    @Serializable(with = BlockedDatesJsonStringSerializer::class)
    val delivery_cutoff_rules: String? = null,

) : Named {
    override fun getConnectedName(): String {
        return getName()
    }

    override fun getActiveState(): Int {
        return active.toInt()
    }

    override fun getConnectedId(): Int {
        return product_id
    }

    @myName("isSunday")
    fun isSunday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return dayLock % 2 == 1
    }

    @myName("isMonday")
    fun isMonday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return (dayLock / 2) % 2 == 1
    }

    @myName("isThusday")
    fun isThusday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return (dayLock / 4) % 2 == 1
    }

    @myName("isWendesday")
    fun isWendesday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return (dayLock / 8) % 2 == 1
    }

    @myName("isThursday")
    fun isThursday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return (dayLock / 16) % 2 == 1
    }

    @myName("isFriday")
    fun isFriday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return (dayLock / 32) % 2 == 1
    }

    @myName("isSaturday")
    fun isSaturday(curDate: String = DatesManipulator.dateNow()): Boolean {
        return (dayLock / 64) % 2 == 1
    }

    @myName("daysString")
    fun daysString(curDate: String = DatesManipulator.dateNow()): List<String> {
        val daysStr = mutableListOf<String>()
        if (isSunday(curDate))
            daysStr.add("א")
        if (isMonday(curDate))
            daysStr.add("ב")
        if (isThusday(curDate))
            daysStr.add("ג")
        if (isWendesday(curDate))
            daysStr.add("ד")
        if (isThursday(curDate))
            daysStr.add("ה")
        if (isFriday(curDate))
            daysStr.add("ו")
        if (isSaturday(curDate))
            daysStr.add("ש")
        return daysStr
    }

    @myName("daysInt")
    fun daysInt(curDate: String = DatesManipulator.dateNow()): List<Int> {
        val daysStr = mutableListOf<Int>()
        if (isSunday(curDate))
            daysStr.add(1)
        if (isMonday(curDate))
            daysStr.add(2)
        if (isThusday(curDate))
            daysStr.add(3)
        if (isWendesday(curDate))
            daysStr.add(4)
        if (isThursday(curDate))
            daysStr.add(5)
        if (isFriday(curDate))
            daysStr.add(6)
        if (isSaturday(curDate))
            daysStr.add(7)
        return daysStr
    }

    override fun toString(): String {
        return getName()
    }

    fun clone(): OrderProduct {
        return this.copy()
    }

    @myName("getProduct")
    open fun getProdoct(): Product {
        return UINKDBInterface.activeDB.getClientProduct(product_id).first!!

    }

    @myName("getName")
    open fun getName(): String {
        return UINKDBInterface.activeDB.getClientProduct(product_id).first!!.getName()

    }

    fun getWrappedUnit(): WrappedName {
        return WrappedName.NONE.convert(extra_unit)
    }

    @myName("getExtraUnitName")
    open fun getExtraUnitName(): String? {

        return if (extra_unit > 0) WrappedName.NONE.convert(extra_unit).MethodName() else null

    }

    @myName("extraUnitOn")
    open fun extraUnitOn(): Boolean {
        return (extra_unit > 0)
    }

    fun getThirdWrappedUnit(): WrappedName {
        return WrappedName.NONE.convert(third_unit)
    }

    @myName("getThirdUnitName")
    open fun getThirdUnitName(): String? {
        return if (third_unit > 0) WrappedName.NONE.convert(third_unit).MethodName() else null
    }

    @myName("thirdUnitOn")
    open fun thirdUnitOn(): Boolean {
        return (third_unit > 0)
    }

    @myName("isAvailable")
    open fun isAvailable(): Boolean {
        return (isActive() && available == (1).toByte())

    }

    @myName("isActive")
    open fun isActive(): Boolean {
        return (active == (1).toByte())
    }

    /** Per-weekday override of [hourLock] for the ORDERING date's Israel weekday (1=Sunday..7=Saturday). */
    private fun dayHourLockFor(orderWeekday1to7: Int): String? {
        return weekdayHourMapValue(day_hour_locks, orderWeekday1to7)
    }

    /** Empty and 00:00 are persisted "no cutoff" values, so precedence must continue. */
    private fun realHourLockOrNull(value: String?): String? {
        val trimmed = value?.trim().orEmpty()
        return trimmed.takeIf { it.isNotEmpty() && it != "00:00" && it != "0:00" }
    }

    /** Recurring cutoff rule for a DELIVERY weekday (1=Sunday..7=Saturday), from [delivery_cutoff_rules].
     * Returns (cutoffWeekday1to7, "HH:mm") when configured, else null. */
    private fun deliveryCutoffRuleFor(deliveryWeekday1to7: Int): Pair<Int, String>? =
        OrderConstraints.deliveryCutoffRuleFor(delivery_cutoff_rules, deliveryWeekday1to7)

    fun isLocked(
        date: String? = null,
        applyTime: Boolean = true,
        /** Company-wide fallback cutoff (already resolved for the ordering weekday, e.g. from
         * [Structs.CofcConf.day_time_locks]), used only when this product has neither [day_hour_locks]
         * nor a flat [hourLock] set for the current order. */
        companyDefaultHourLock: String? = null,
        /** Company-wide [Structs.CofcConf.delivery_cutoff_rules], used when this product has no
         * rule of its own for the target delivery weekday. */
        companyDeliveryCutoffRules: String? = null,
    ): Boolean {
        // existing "day" rule
        val day = date != null &&
                !daysInt().contains((DatesManipulator.getDayOfWeekIsrael(date) % 7 + 1))

        val curDate = DatesManipulator.dateNowNoHour()            // "yyyy-MM-dd"
        val nextDate = date ?: curDate

        // cutoff precedence: per-order-day product override -> flat product hourLock -> company default
        val orderWeekday1to7 = DatesManipulator.getDayOfWeekIsrael(curDate) % 7 + 1
        val effectiveHourLock = realHourLockOrNull(dayHourLockFor(orderWeekday1to7))
            ?: realHourLockOrNull(hourLock)
            ?: realHourLockOrNull(companyDefaultHourLock)
            ?: ""

        // existing "next day after hourLock" rule (inclusive cutoff minute, see Utils.timeLock)
        val lock_time = applyTime &&
                effectiveHourLock.isNotBlank() &&
                DatesManipulator.getNextDay(curDate, 1) == nextDate &&
                timeLock(effectiveHourLock)

        // NEW: limit how many days ahead can be ordered
        val aheadLock = if ( days_ahead>0 && date != null) {
            val today = LocalDate.parse(curDate)                  // today
            val target = LocalDate.parse(date)                    // requested date
            val diffDays = today.daysUntil(target)                // days in future

            diffDays < days_ahead                                // lock if date > days_ahead
        } else {
            false
        }

        // NEW: recurring delivery-weekday cutoff ("order until Thursday 14:00 for Sunday delivery").
        // When configured for the target's delivery weekday, this REPLACES days_ahead/hourLock above
        // for that weekday. `day` (this product's enabled delivery weekdays) still applies untouched.
        val deliveryWeekday1to7 = date?.let { DatesManipulator.getDayOfWeekIsrael(it) % 7 + 1 }
        val cutoffRule = deliveryWeekday1to7?.let { wd ->
            deliveryCutoffRuleFor(wd) ?: OrderConstraints.deliveryCutoffRuleFor(companyDeliveryCutoffRules, wd)
        }
        val cutoffLock: Boolean? = if (applyTime && cutoffRule != null && date != null) {
            val (cutoffWeekday, cutoffTimeStr) = cutoffRule
            var daysBack = ((deliveryWeekday1to7 - cutoffWeekday) % 7 + 7) % 7
            if (daysBack == 0) daysBack = 7
            val cutoffDateIso = DatesManipulator.getNextDay(date, -daysBack)
            when {
                curDate > cutoffDateIso -> true
                curDate < cutoffDateIso -> false
                else -> timeLock(cutoffTimeStr)
            }
        } else null

        val leadOrHourLock = cutoffLock ?: (lock_time || aheadLock)

        return (forceLock == 1) || leadOrHourLock || day
    }

    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()
        map["id"] = JsonPrimitive(id.toString())
        map["product_id"] = JsonPrimitive(product_id.toString())
        map["extra_unit"] = JsonPrimitive(extra_unit.toString())
        map["conversion_rate"] = JsonPrimitive(conversion_rate.toString())
        map["weight"] = JsonPrimitive(weight.toString())
        map["englishName"] = JsonPrimitive(englishName.toString())
        map["available"] = JsonPrimitive(available.toString())
        map["active"] = JsonPrimitive(active.toString())

        return JsonObject(map)
    }

    companion object {
        fun toJsonArrayString(pd: List<OrderProduct>): String {
            return if (pd.isEmpty()) {
                "[]"
            } else {
                val payload = pd.map {
                    it.toJson()
                }.joinToString(",")
                return "[$payload]"
            }

        }
    }
}


